type ErrorLike = {
  name?: string
  message?: string
  stack?: string
  code?: unknown
}

function asErrorLike(value: unknown): ErrorLike | null {
  if (!value || typeof value !== 'object') {
    return null
  }

  return value as ErrorLike
}

function getErrorStack(value: unknown): string {
  const errorLike = asErrorLike(value)
  return typeof errorLike?.stack === 'string' ? errorLike.stack : ''
}

function getErrorMessage(value: unknown): string {
  if (typeof value === 'string') {
    return value
  }

  const errorLike = asErrorLike(value)
  return typeof errorLike?.message === 'string' ? errorLike.message : ''
}

export function isExpectedNotFound(url: string | undefined, status: number | undefined): boolean {
  return status === 404 && Boolean(url?.split('?')[0].endsWith('/market-makers/self'))
}

export function isBrowserExtensionError(value: unknown): boolean {
  const stack = getErrorStack(value)
  if (
    stack.includes('chrome-extension://') ||
    stack.includes('moz-extension://') ||
    stack.includes('safari-extension://')
  ) {
    return true
  }

  const message = getErrorMessage(value)
  return message.includes('provider is disconnected from all chains')
}

export function isHandledMutationApiError(value: unknown): boolean {
  const errorLike = asErrorLike(value)
  return errorLike?.name === 'ApiHttpError'
}

type SentryExceptionLike = {
  type?: string
  value?: string
  mechanism?: { type?: string }
  stacktrace?: { frames?: Array<{ filename?: string; function?: string }> }
}

type SentryEventLike = {
  exception?: {
    values?: SentryExceptionLike[]
  }
  context?: Record<string, unknown>
}

function isInjectedMobileWebViewError(exception: SentryExceptionLike): boolean {
  const frames = exception.stacktrace?.frames ?? []

  if (
    exception.type === 'ReferenceError' &&
    exception.value === 'xbrowser is not defined' &&
    frames.some(
      (frame) => frame.filename === '<anonymous>' && frame.function === 'execute_auto_fill',
    ) &&
    frames.every((frame) => !frame.filename || frame.filename === '<anonymous>')
  ) {
    return true
  }

  if (frames.some((frame) => frame.filename?.startsWith('iabjs://'))) {
    return true
  }

  if (frames.some((frame) => frame.function === 'Object.setContactAutofillValuesFromBridge')) {
    return true
  }

  return (
    exception.value?.includes('window.webkit.messageHandlers') === true &&
    frames.some((frame) => frame.function === 'sendDataToNative')
  )
}

function getSerializedRejectionReason(event: SentryEventLike): unknown {
  const serialized = event.context?.__serialized__
  if (!serialized || typeof serialized !== 'object') {
    return null
  }

  return serialized
}

export function shouldReportSentryEvent(event: SentryEventLike): boolean {
  const serializedReason = getSerializedRejectionReason(event)
  if (serializedReason) {
    if (isBrowserExtensionError(serializedReason) || isHandledMutationApiError(serializedReason)) {
      return false
    }
  }

  const exceptionValues = event.exception?.values ?? []

  for (const exception of exceptionValues) {
    if (isInjectedMobileWebViewError(exception)) {
      return false
    }

    if (isBrowserExtensionError({ message: exception.value })) {
      return false
    }

    if (
      exception.type === 'ApiHttpError' &&
      exception.mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection'
    ) {
      return false
    }

    const frames = exception.stacktrace?.frames ?? []
    for (const frame of frames) {
      const filename = frame.filename ?? ''
      if (
        filename.includes('chrome-extension://') ||
        filename.includes('moz-extension://') ||
        filename.includes('safari-extension://')
      ) {
        return false
      }
    }
  }

  return true
}
